/**
 * ScriptPad app shell: an editable page plus file operations, autosave with
 * crash recovery, and a PWA update prompt. One document open at a time.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EditorView } from "prosemirror-view";
import { createNewDocument } from "../format/newDocument";
import { DocumentSession } from "./session";
import type { Node as PMNode } from "prosemirror-model";
import { EditorSurface, type EditorChangeInfo } from "../editor/EditorView";
import { setElementType } from "../editor/commands";
import { estimatePageCount } from "../editor/pagecount";
import { toFountain } from "../format/fountain";
import { readTitlePage } from "../format/titlepage";
import type { Para } from "../format/types";
import { Toolbar } from "./Toolbar";
import { StatusBar } from "./StatusBar";
import { ElementSwitcher } from "./ElementSwitcher";
import { TitlePageDialog } from "./TitlePageDialog";
import { RenameDialog } from "./RenameDialog";
import { ToastStack, useToasts } from "./Toast";
import { useKeyboardInset } from "./useKeyboardInset";

const DEFAULT_FILENAME = "untitled.fadein";
import {
  openFadeinFile,
  sessionFromXml,
  downloadBytes,
  downloadText,
  withExtension,
  shareBytes,
  canShareFiles,
} from "./files";

/** Lightweight Para[] view of the live PM doc (for page count / export). */
function scanParas(doc: PMNode): Para[] {
  const paras: Para[] = [];
  doc.forEach((node) => {
    if (node.type.name === "para") {
      paras.push({ style: String(node.attrs.styleName), runs: [{ text: node.textContent }] });
    }
  });
  return paras;
}
import {
  Autosaver,
  writeSnapshot,
  recoveryCandidate,
  type SnapshotInput,
} from "../storage/autosave";
import { putRecent, listRecent } from "../storage/recent";
import { setupPWA } from "../pwa";
import "./app.css";

export function App() {
  const [session, setSession] = useState<DocumentSession>(
    () => new DocumentSession(createNewDocument()),
  );
  const viewRef = useRef<EditorView | null>(null);
  const [ui, setUi] = useState<EditorChangeInfo>({ elementType: null, dirty: false });
  const [canShare, setCanShare] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [showTitlePage, setShowTitlePage] = useState(false);
  const [filename, setFilename] = useState(session.filename);
  // When set, the rename dialog is open; "save" means save right after renaming.
  const [renaming, setRenaming] = useState<null | { purpose: "rename" | "save" }>(null);
  const { toasts, show, dismiss } = useToasts();

  useKeyboardInset();

  const zoomBy = useCallback((delta: number) => {
    setZoom((z) => Math.min(1.6, Math.max(0.6, Math.round((z + delta) * 10) / 10)));
  }, []);

  const onPickElement = useCallback(
    (styleName: string) => {
      const view = viewRef.current;
      if (!view) return;
      setElementType(session.styles, styleName)(view.state, view.dispatch, view);
      view.focus();
    },
    [session],
  );

  // ---- Autosave (debounced snapshots, recreated per document) ----
  const autosaver = useMemo(
    () =>
      new Autosaver((): SnapshotInput => {
        const view = viewRef.current;
        return {
          uuid: session.uuid,
          xml: view ? session.serialize(view.state) : "",
          filename: session.filename,
        };
      }),
    [session],
  );
  useEffect(() => () => autosaver.cancel(), [autosaver]);

  const recomputePages = useCallback(() => {
    const view = viewRef.current;
    if (view) setPageCount(estimatePageCount(scanParas(view.state.doc), session.metrics));
  }, [session]);

  const onDocChanged = useCallback(() => autosaver.schedule(), [autosaver]);
  const onChange = useCallback(
    (info: EditorChangeInfo) => {
      setUi(info);
      recomputePages();
    },
    [recomputePages],
  );

  // ---- Load a different document (new / open / recover) ----
  const loadSession = useCallback(
    (next: DocumentSession) => {
      autosaver.cancel();
      setSession(next);
      setFilename(next.filename);
      setUi({ elementType: null, dirty: false });
    },
    [autosaver],
  );

  /** Apply a new filename to the session and the UI. */
  const applyRename = useCallback(
    (name: string) => {
      session.filename = name;
      setFilename(name);
      session.markDirty();
      setUi((u) => ({ ...u, dirty: true }));
    },
    [session],
  );

  const onNew = useCallback(() => {
    if (session.dirty && !confirm("Discard unsaved changes and start a new script?")) {
      return;
    }
    loadSession(new DocumentSession(createNewDocument()));
  }, [session, loadSession]);

  const onOpen = useCallback(
    async (file: File) => {
      try {
        const next = await openFadeinFile(file);
        loadSession(next);
        show({ kind: "success", message: `Opened ${file.name}`, timeout: 2500 });
      } catch (err) {
        show({
          kind: "error",
          message: `Could not open ${file.name}: ${(err as Error).message}`,
          timeout: 8000,
        });
      }
    },
    [loadSession, show],
  );

  const persistSave = useCallback(
    async (kind: "save") => {
      const view = viewRef.current;
      if (!view) return null;
      const xml = session.serialize(view.state, pageCount);
      const ts = Date.now();
      await writeSnapshot({ uuid: session.uuid, xml, filename: session.filename, ts, kind });
      await putRecent({ uuid: session.uuid, filename: session.filename, ts, xml });
      session.markSaved();
      setUi((u) => ({ ...u, dirty: false }));
      return xml;
    },
    [session, pageCount],
  );

  const doSave = useCallback(async () => {
    const view = viewRef.current;
    if (!view) return;
    try {
      const bytes = session.toBytes(view.state, pageCount);
      downloadBytes(bytes, session.filename);
      await persistSave("save");
      show({ kind: "success", message: `Saved ${session.filename}`, timeout: 2000 });
    } catch (err) {
      show({ kind: "error", message: `Save failed: ${(err as Error).message}`, timeout: 8000 });
    }
  }, [session, pageCount, persistSave, show]);

  // Save, but prompt for a name first if the document is still untitled.
  const onSave = useCallback(() => {
    if (session.filename === DEFAULT_FILENAME) {
      setRenaming({ purpose: "save" });
    } else {
      void doSave();
    }
  }, [session, doSave]);

  const onRename = useCallback(() => setRenaming({ purpose: "rename" }), []);

  const onExportFountain = useCallback(() => {
    const view = viewRef.current;
    if (!view) return;
    const text = toFountain(scanParas(view.state.doc), readTitlePage(session.dom));
    downloadText(text, withExtension(session.filename, ".fountain"));
  }, [session]);

  const onPrint = useCallback(() => window.print(), []);
  const onTitlePage = useCallback(() => setShowTitlePage(true), []);

  const onShare = useCallback(async () => {
    const view = viewRef.current;
    if (!view) return;
    const bytes = session.toBytes(view.state, pageCount);
    try {
      const shared = await shareBytes(bytes, session.filename);
      if (shared) {
        await persistSave("save");
        show({ kind: "success", message: "Shared", timeout: 2000 });
      } else {
        downloadBytes(bytes, session.filename);
      }
    } catch (err) {
      show({ kind: "error", message: `Share failed: ${(err as Error).message}`, timeout: 8000 });
    }
  }, [session, pageCount, persistSave, show]);

  // Detect share capability once a document is present.
  useEffect(() => {
    const view = viewRef.current;
    if (view) setCanShare(canShareFiles(session.toBytes(view.state), session.filename));
  }, [session]);

  // ---- Crash recovery offer on launch ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const recents = await listRecent();
      if (cancelled || recents.length === 0) return;
      const newest = recents[0]!;
      const candidate = await recoveryCandidate(newest.uuid);
      if (cancelled || !candidate) return;
      show({
        kind: "info",
        message: `Restore unsaved changes to “${candidate.filename}”?`,
        action: {
          label: "Restore",
          run: () => loadSession(sessionFromXml(candidate.xml, candidate.filename)),
        },
      });
    })();
    return () => {
      cancelled = true;
    };
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- PWA update prompt (production only) ----
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    const controller = setupPWA({
      onNeedRefresh() {
        show({
          kind: "info",
          message: "A new version is available.",
          action: { label: "Update", run: () => controller.update() },
        });
      },
      onOfflineReady() {
        show({ kind: "success", message: "Ready to work offline", timeout: 3000 });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="sp-app">
      <Toolbar
        filename={filename}
        dirty={ui.dirty}
        canShare={canShare}
        onNew={onNew}
        onOpen={onOpen}
        onSave={onSave}
        onShare={onShare}
        onTitlePage={onTitlePage}
        onExportFountain={onExportFountain}
        onPrint={onPrint}
        onRename={onRename}
      />
      <main className="sp-main">
        <EditorSurface
          session={session}
          viewRef={viewRef}
          onChange={onChange}
          onDocChanged={onDocChanged}
          zoom={zoom}
        />
      </main>
      <div className="sp-bottom">
        <ElementSwitcher
          styles={session.styles}
          current={ui.elementType}
          onPick={onPickElement}
        />
        <StatusBar
          elementType={ui.elementType}
          pageCount={pageCount}
          saved={!ui.dirty}
          zoom={zoom}
          onZoomIn={() => zoomBy(0.1)}
          onZoomOut={() => zoomBy(-0.1)}
        />
      </div>
      {showTitlePage && (
        <TitlePageDialog
          session={session}
          onClose={() => setShowTitlePage(false)}
          onSaved={() => {
            setUi((u) => ({ ...u, dirty: true }));
            autosaver.schedule();
          }}
        />
      )}
      {renaming && (
        <RenameDialog
          initial={session.filename === DEFAULT_FILENAME ? "" : session.filename}
          title={renaming.purpose === "save" ? "Name your script" : "Rename script"}
          confirmLabel={renaming.purpose === "save" ? "Save" : "Rename"}
          onSubmit={(name) => {
            applyRename(name);
            if (renaming.purpose === "save") void doSave();
          }}
          onClose={() => setRenaming(null)}
        />
      )}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
